/**
 * The ORGANISATION being worked in — the outermost of the three header scopes.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE NESTED SELECTORS, AND THIS IS THE ONE THAT ALSO DECIDES WHERE WRITES GO
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Organisation → Site → Plant mirrors the hierarchy exactly. The other two narrow what you are
 * LOOKING at. This one additionally decides what you are WORKING IN: creating a site or inviting
 * a person while a descendant is selected puts the new row in that descendant.
 *
 * That is not a convenience. A sub-organisation is created empty, and before this existed there
 * was no API and no screen that could put a first site or a first person into one — it stayed
 * empty forever. The selector is what makes the hierarchy usable rather than decorative.
 *
 * ── AMBIENT IS NOT ENOUGH, SO THE FORMS SAY IT TOO ──────────────────────────
 * Every create form states "Creating in <name>" and offers to change it. A header that silently
 * retargets a create is how somebody builds a site in the wrong company and finds out a week
 * later. The header sets the default; the form makes it visible at the moment it matters.
 *
 * ── HIDDEN WHEN IT CANNOT ACT ────────────────────────────────────────────────
 * `visible` is false for a caller whose reach is one organisation, which is every caller until
 * somebody creates a child. Same house rule as the industry switcher: a control that can only do
 * one thing is chrome, not a control.
 *
 * ── AND HIDDEN FROM WHOEVER IT ISN'T FOR ─────────────────────────────────────
 * `visible` is ALSO false for a caller who does not hold `tenants.edit` — the same permission
 * that gates the Organisation screen and every organisation edit — however many organisations
 * they reach. Switching where a write lands is an administrative act, not a viewing one: reads
 * already widen down the whole subtree with no switch at all (`lib/org-reach.ts`, lumen-be), so a
 * Field Engineer who cannot act on a sub-organisation was never missing any DATA by not having
 * this control — only a control whose only use is to redirect a create he already isn't allowed
 * to make. See `org-context.tsx`'s file doc for how that was measured and why gating it costs a
 * non-admin nothing.
 *
 * The two conditions are independent, not layered: a caller can fail either one and the result is
 * the same hidden control, never a disabled one.
 *
 * Separate from the provider so each module exports one kind of thing, which is what React Fast
 * Refresh needs to hot-reload either of them.
 */
import { createContext, useContext } from 'react';

export interface OrgNode {
  id: string;
  name: string;
  slug: string;
  /** Null for a root. The Access screen draws its tree from the same shape. */
  parentId: string | null;
}

export interface OrgContextValue {
  /** Every organisation in reach, flat, ordered by name. */
  organisations: OrgNode[];
  /** The one being worked in. Never null — it falls back to `home`. */
  selectedId: string;
  selected: OrgNode | null;
  /** The caller's own organisation. They may always act here. */
  homeId: string;
  /** Holds `tenants.edit` AND reaches more than one organisation — see the file doc above. */
  visible: boolean;
  loading: boolean;
  select: (id: string) => void;
}

export const OrgContext = createContext<OrgContextValue | null>(null);

export function useOrgContext(): OrgContextValue {
  const v = useContext(OrgContext);
  if (!v) throw new Error('useOrgContext must be used inside <OrgProvider>');
  return v;
}
